/**
 * ThemeSwitcher — tri-state theme toggle (Light / Dark / System), Layer-1 shared.
 * @layer-1 — reusable anywhere; one change propagates to all consumers.
 *
 * Ported from the personal portfolio toggle:
 *   - Smart 3-state cycle (cycleTheme) so the first click always flips the visual.
 *   - Three stacked icons (Sun / Moon / "A") cross-faded via [data-state] + CSS.
 *   - Circular page wipe (applyThemeWithWipe → View Transitions API).
 *
 * Flow (click):
 *   1. next = cycleTheme(currentMode, systemDark)
 *   2. entering "system" → show the "A" glyph for 1s, then resolve to Sun/Moon
 *   3. applyThemeWithWipe(next) — persists to localStorage (guests need no backend)
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Moon } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useTheme } from "@/shared/hooks/useTheme";
import { useReducedMotion } from "@/shared/hooks/useReducedMotion";
import { applyThemeWithWipe, cycleTheme, resolveIsDark } from "@/shared/lib/theme";

const AUTO_GLYPH_MS = 1000;

export function ThemeSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const { mode } = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const [showAuto, setShowAuto] = useState(false);
  const autoTimer = useRef<number | undefined>(undefined);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Clear the pending "A → resolved" timer on unmount.
  useEffect(() => () => window.clearTimeout(autoTimer.current), []);

  // Visible icon: "auto" only during the 1s window after entering system mode;
  // otherwise the concrete resolved dark/light icon.
  const dataState =
    mode === "system" && showAuto ? "auto" : resolveIsDark(mode) ? "dark" : "light";

  const handleClick = () => {
    const next = cycleTheme(mode, resolveIsDark("system"));
    window.clearTimeout(autoTimer.current);
    if (next === "system" && !prefersReducedMotion) {
      setShowAuto(true);
      autoTimer.current = window.setTimeout(() => setShowAuto(false), AUTO_GLYPH_MS);
    } else {
      setShowAuto(false);
    }
    const rect = btnRef.current?.getBoundingClientRect();
    applyThemeWithWipe(
      next,
      rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : undefined,
    );
  };

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={handleClick}
      aria-label={t("themeToggle.label")}
      data-state={dataState}
      className={cn(
        "theme-switcher relative flex h-9 w-9 items-center justify-center rounded-lg",
        "transition-colors cursor-pointer bg-sidebar-bg text-sidebar-text hover:bg-sidebar-hover-bg",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        !prefersReducedMotion && "active:scale-95",
        className,
      )}
    >
      <span className="ts-icon ts-sun" aria-hidden="true">
        <Sun className="h-4 w-4" />
      </span>
      <span className="ts-icon ts-moon" aria-hidden="true">
        <Moon className="h-4 w-4" />
      </span>
      <span className="ts-icon ts-auto" aria-hidden="true">
        A
      </span>
    </button>
  );
}
